import React, { useEffect, useState } from 'react';
import { AnimatePresence, LazyMotion, MotionConfig, domAnimation, m } from 'framer-motion';
import './App.css';
import './Home.css';

const EASE_OUT = [0.22, 1, 0.36, 1];

const APP_STORE_URL = 'https://apps.apple.com/us/app/walk-it-off-daily-walk-habit/id6818257483';

// Photos from Unsplash (Unsplash License).
const HERO_PHOTO = 'https://images.unsplash.com/photo-1761055922823-1a5ff0dc2541';
const TOGETHER_PHOTO = 'https://images.unsplash.com/photo-1758613170846-e468188aee84';

// Sections fade/slide in once as they enter the viewport and stagger their children.
const sectionReveal = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: EASE_OUT, when: 'beforeChildren', staggerChildren: 0.1 }
  }
};

const itemReveal = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE_OUT } }
};

const revealProps = {
  variants: sectionReveal,
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.15, margin: '0px 0px -80px 0px' }
};

// Hover/tap transitions are declared inline so they never override an
// element's own entrance transition.
const SPRING = { type: 'spring', stiffness: 400, damping: 28 };

const buttonMotion = {
  whileHover: { y: -2, transition: SPRING },
  whileTap: { scale: 0.97, transition: SPRING }
};

const cardMotion = {
  whileHover: { y: -6, transition: { type: 'spring', stiffness: 300, damping: 26 } }
};

const GOAL_MIN = 3000;
const GOAL_MAX = 15000;
const GOAL_STEP = 500;

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Sample week used by the illustrative app previews. Null means the day hasn't happened yet.
const SAMPLE_GOAL = 7000;
const SAMPLE_WEEK = [8210, 7145, 7302, 7480, 4820, null, null];

const HERO_COPY = {
  universal: {
    title: 'Make walking part of your day.',
    subtitle: 'A plan that fits your life. Progress you can see. People who help you keep going.'
  },
  prediabetes: {
    title: 'You know you should walk more. Now you will.',
    subtitle: 'Your doctor told you to walk more. Knowing and doing are different things. Walk It Off helps you turn it into a daily habit.'
  },
  fitness: {
    title: 'Stop setting goals. Start keeping them.',
    subtitle: 'Track your consistency, join challenges with friends, and make every day count.'
  }
};

const FAQS = [
  {
    q: 'Do I need a smartwatch?',
    a: 'No. Your iPhone counts your steps on its own, and Walk It Off reads them from Apple Health. If you wear an Apple Watch, those steps count too.'
  },
  {
    q: 'Is Walk It Off available on Android?',
    a: 'Not yet. Walk It Off is on iPhone today. Join the Android waitlist and we\'ll email you when it lands.'
  },
  {
    q: 'Can I walk at my own pace?',
    a: 'Yes. Pick a daily goal anywhere from 3,000 to 15,000 steps and change it whenever life changes. Wheelchair users can count pushes instead of steps on iPhone.'
  }
];

const Icon = {
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  heart: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  people: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" /><circle cx="17" cy="9" r="2.5" />
      <path d="M2 19c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5v1H2zM16.5 13.6c3 .2 5.5 1.9 5.5 4.6v1.8h-4.5v-1c0-2-.4-3.8-1-5.4z" />
    </svg>
  ),
  bars: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M6 20v-6M12 20V8M18 20V4" />
    </svg>
  ),
  shoe: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 17.5c0-2.6.7-5.6 1.6-8.1.3-.9 1.3-1.3 2.1-.9l1.8.9c.6.3 1.3.2 1.8-.2l1-.9c.5-.4 1.2-.3 1.5.2.9 1.4 2.3 2.6 4 3.2l2.7 1c1.5.5 2.5 1.9 2.5 3.5v1.3c0 .8-.7 1.5-1.5 1.5H4.5c-.8 0-1.5-.7-1.5-1.5z" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  ),
  footprints: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <ellipse cx="8" cy="7" rx="3" ry="4.5" /><ellipse cx="8" cy="15.5" rx="2.2" ry="2" />
      <ellipse cx="16" cy="10" rx="3" ry="4.5" /><ellipse cx="16" cy="18.5" rx="2.2" ry="2" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  apple: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.54c-.03-2.75 2.25-4.08 2.35-4.14-1.28-1.87-3.27-2.13-3.98-2.16-1.69-.17-3.31 1-4.17 1-.86 0-2.19-.98-3.6-.95-1.85.03-3.56 1.08-4.51 2.73-1.92 3.34-.49 8.28 1.38 10.99.92 1.33 2.01 2.82 3.44 2.76 1.38-.05 1.9-.89 3.57-.89 1.67 0 2.14.89 3.6.86 1.49-.02 2.43-1.35 3.34-2.69 1.05-1.54 1.48-3.04 1.51-3.11-.03-.02-2.9-1.11-2.93-4.4zM14.3 4.47c.76-.92 1.27-2.2 1.13-3.47-1.09.04-2.42.73-3.2 1.65-.7.81-1.32 2.11-1.15 3.36 1.22.09 2.46-.62 3.22-1.54z" />
    </svg>
  ),
  android: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6 18c0 .55.45 1 1 1h1v3.5a1.5 1.5 0 0 0 3 0V19h2v3.5a1.5 1.5 0 0 0 3 0V19h1c.55 0 1-.45 1-1V8H6zM3.5 8A1.5 1.5 0 0 0 2 9.5v7a1.5 1.5 0 0 0 3 0v-7A1.5 1.5 0 0 0 3.5 8zm17 0A1.5 1.5 0 0 0 19 9.5v7a1.5 1.5 0 0 0 3 0v-7A1.5 1.5 0 0 0 20.5 8zm-4.97-5.84l1.3-1.3a.5.5 0 0 0-.7-.71l-1.48 1.48A5.96 5.96 0 0 0 12 1c-.96 0-1.86.23-2.66.63L7.85.15a.5.5 0 0 0-.7.7l1.31 1.31A5.97 5.97 0 0 0 6 7h12a5.97 5.97 0 0 0-2.47-4.84zM10 5H9V4h1zm5 0h-1V4h1z" />
    </svg>
  )
};

// Progress ring used in the illustrative app previews.
function Ring({ value, size = 28, stroke = 4, children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = value == null ? 0 : Math.min(value, 1);
  return (
    <span className="lp-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--lp-ring-track)" strokeWidth={stroke} />
        {pct > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={pct >= 1 ? 'var(--lp-teal)' : 'var(--lp-teal-soft)'}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${c * pct} ${c}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      {children}
    </span>
  );
}

function WeekRings({ size, stroke, showSteps = false }) {
  return (
    <div className="lp-week">
      {DAYS.map((day, i) => {
        const steps = SAMPLE_WEEK[i];
        return (
          <div className="lp-week-day" key={day}>
            <span className="lp-week-label">{day}</span>
            <Ring value={steps == null ? null : steps / SAMPLE_GOAL} size={size} stroke={stroke} />
            {showSteps && <span className="lp-week-steps">{steps == null ? '–' : steps.toLocaleString('en-US')}</span>}
          </div>
        );
      })}
    </div>
  );
}

function AppStoreBadge({ className = '' }) {
  return (
    <a className={`lp-store-badge ${className}`} href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
      {Icon.apple}
      <span>
        <small>Download on the</small>
        App Store
      </span>
    </a>
  );
}

function App() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [pageVariant, setPageVariant] = useState('universal');
  const [goal, setGoal] = useState(SAMPLE_GOAL);

  const DISCORD_WEBHOOK_URL = process.env.REACT_APP_DISCORD_WEBHOOK_URL;
  const currentYear = new Date().getFullYear();
  const hero = HERO_COPY[pageVariant];
  const daysReached = SAMPLE_WEEK.filter((s) => s != null && s >= SAMPLE_GOAL).length;
  const weekTotal = SAMPLE_WEEK.reduce((sum, s) => sum + (s || 0), 0);
  const daysLogged = SAMPLE_WEEK.filter((s) => s != null).length;

  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);

    if (path.includes('/prediabetes') || params.get('variant') === 'prediabetes') {
      setPageVariant('prediabetes');
    } else if (path.includes('/fitness-challenge') || params.get('variant') === 'fitness') {
      setPageVariant('fitness');
    } else {
      setPageVariant('universal');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (DISCORD_WEBHOOK_URL) {
        const response = await fetch(DISCORD_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            embeds: [{
              title: '🎯 New Walk It Off Waitlist Signup!',
              color: 0x0D7377,
              fields: [{ name: '📧 Email', value: email, inline: true }],
              timestamp: new Date().toISOString(),
              footer: { text: 'Walk It Off Waitlist' }
            }]
          })
        });
        if (!response.ok) console.error('Failed to send to Discord');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setFormSubmitted(true);
      setEmail('');
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openWaitlist = () => {
    setShowWaitlist(true);
    scrollToSection('get-the-app');
  };

  return (
    // LazyMotion + `m` ships only the DOM animation features this page uses,
    // which is roughly a third of the size of the full `motion` bundle.
    <LazyMotion features={domAnimation} strict>
    <MotionConfig reducedMotion="user">
    <div className="App lp">
      <header className="lp-nav">
        <div className="lp-shell lp-nav-inner">
          <a href="/" className="lp-nav-logo" aria-label="Walk It Off home">
            <img src="/walkitofflogo-dark.svg" alt="Walk It Off" width="350" height="130" />
          </a>
          <nav className="lp-nav-links" aria-label="Main">
            <a href="#how-it-works">How it works</a>
            <a href="#progress">Your progress</a>
            <a href="#together">Walk together</a>
            <a href="#faq">FAQ</a>
            <a href="/support">Support</a>
          </nav>
          <m.a className="lp-btn lp-btn-navy" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" {...buttonMotion}>
            Get the app
          </m.a>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-shell lp-hero-grid">
            <m.div className="lp-hero-copy" variants={sectionReveal} initial="hidden" animate="visible">
              <m.p className="lp-eyebrow" variants={itemReveal}>Small walks. A routine that lasts.</m.p>
              <m.h1 className="lp-hero-title" variants={itemReveal}>{hero.title}</m.h1>
              <m.p className="lp-hero-subtitle" variants={itemReveal}>{hero.subtitle}</m.p>
              <m.div className="lp-hero-actions" variants={itemReveal}>
                <AppStoreBadge />
                <button type="button" className="lp-link" onClick={() => scrollToSection('how-it-works')}>
                  See how it works <span aria-hidden="true">↗</span>
                </button>
              </m.div>
              <m.p className="lp-hero-note" variants={itemReveal}>
                Available on iPhone · <button type="button" className="lp-inline-link" onClick={openWaitlist}>Android waitlist</button>
              </m.p>
            </m.div>

            <m.div
              className="lp-hero-visual"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.15 }}
            >
              <img
                className="lp-hero-photo"
                src={`${HERO_PHOTO}?w=1100&q=75&auto=format&fit=crop`}
                srcSet={`${HERO_PHOTO}?w=600&q=70&auto=format&fit=crop 600w, ${HERO_PHOTO}?w=1100&q=75&auto=format&fit=crop 1100w`}
                sizes="(max-width: 900px) 100vw, 50vw"
                alt="Two friends smiling as they walk arm in arm through a park"
                width="1100"
                height="1650"
                fetchPriority="high"
              />
              <div className="lp-phone" aria-hidden="true">
                <div className="lp-phone-notch" />
                <div className="lp-phone-status"><span>9:41</span></div>
                <div className="lp-phone-brand"><span className="lp-phone-mark">{Icon.footprints}</span>WALK IT OFF</div>
                <p className="lp-phone-today">Today</p>
                <Ring value={4820 / SAMPLE_GOAL} size={128} stroke={12}>
                  <span className="lp-ring-center">
                    <span className="lp-ring-icon">{Icon.footprints}</span>
                    <strong>4,820</strong>
                    <small>steps</small>
                  </span>
                </Ring>
                <p className="lp-phone-goal">Goal 7,000</p>
                <div className="lp-phone-card">
                  <span className="lp-phone-card-icon">{Icon.shoe}</span>
                  <span><strong>Next walk</strong><small>Make time for a short walk</small></span>
                  <span className="lp-phone-chevron">›</span>
                </div>
                <div className="lp-phone-btn">View my plan</div>
                <p className="lp-phone-week-title">This week</p>
                <WeekRings size={18} stroke={3} />
              </div>
              <p className="lp-hero-caption">Illustrative app preview</p>
            </m.div>
          </div>
        </section>

        <m.section className="lp-shell lp-highlights" aria-label="Highlights" {...revealProps}>
          {[
            { icon: Icon.target, title: 'Your own walking goal', text: 'Set a goal that fits your life.' },
            { icon: Icon.heart, title: 'Steps synced with Apple Health', text: 'Your steps, counted automatically.', tone: 'rose' },
            { icon: Icon.people, title: 'Friends and challenges', text: 'More support for the days ahead.' }
          ].map((item) => (
            <m.div className="lp-highlight" key={item.title} variants={itemReveal}>
              <span className={`lp-highlight-icon ${item.tone ? `is-${item.tone}` : ''}`}>{item.icon}</span>
              <span>
                <strong>{item.title}</strong>
                <small>{item.text}</small>
              </span>
            </m.div>
          ))}
        </m.section>

        <m.section className="lp-shell lp-features" {...revealProps}>
          <m.h2 className="lp-h2" variants={itemReveal}>A little structure. More reasons to keep going.</m.h2>
          <div className="lp-feature-grid">
            <m.article className="lp-feature is-mint" variants={itemReveal} {...cardMotion}>
              <h3>Make it manageable</h3>
              <p>Set a daily step goal that fits your life, and change it whenever you need to.</p>
              <div className="lp-feature-panel">
                <span className="lp-panel-label">Your goal</span>
                <div className="lp-stepper">
                  <button type="button" aria-label="Lower goal" onClick={() => setGoal((g) => Math.max(GOAL_MIN, g - GOAL_STEP))} disabled={goal <= GOAL_MIN}>−</button>
                  <output aria-live="polite">
                    <strong>{goal.toLocaleString('en-US')}</strong>
                    <small>steps</small>
                  </output>
                  <button type="button" aria-label="Raise goal" onClick={() => setGoal((g) => Math.min(GOAL_MAX, g + GOAL_STEP))} disabled={goal >= GOAL_MAX}>+</button>
                </div>
                <span className="lp-panel-note">Anywhere from 3,000 to 15,000</span>
              </div>
            </m.article>

            <m.article className="lp-feature is-cream" variants={itemReveal} {...cardMotion}>
              <h3>See your consistency</h3>
              <p>Follow your walking week, one day at a time.</p>
              <div className="lp-feature-panel">
                <WeekRings size={26} stroke={4} />
                <div className="lp-panel-row">
                  <span>
                    <strong className="lp-panel-big">{daysReached} of 7</strong>
                    <small>days reached</small>
                  </span>
                  <span className="lp-panel-chip">{Icon.bars}</span>
                </div>
              </div>
            </m.article>

            <m.article className="lp-feature is-mint" variants={itemReveal} {...cardMotion}>
              <h3>Keep good company</h3>
              <p>Cheer on friends and join a walking challenge.</p>
              <div className="lp-feature-panel">
                <div className="lp-avatars">
                  <span className="lp-avatar is-a">AM</span>
                  <span className="lp-avatar is-b">KO</span>
                  <span className="lp-avatar is-c">SR</span>
                  <span className="lp-avatar is-more">+2</span>
                </div>
                <div className="lp-cheer">
                  <span className="lp-avatar is-d is-small">J</span>
                  <span>
                    <strong>Jordan</strong>
                    <small>Just finished my walk!</small>
                  </span>
                  <span className="lp-cheer-emoji" aria-hidden="true">🙌</span>
                </div>
              </div>
            </m.article>
          </div>
        </m.section>

        <m.section id="progress" className="lp-progress" {...revealProps}>
          <div className="lp-shell lp-progress-grid">
            <m.div variants={itemReveal}>
              <h2 className="lp-h2 lp-h2-lg">Your progress,<br />beyond a single number.</h2>
              <p className="lp-lead">See your daily steps and weekly patterns. Adjust your routine as life changes.</p>
            </m.div>
            <m.div className="lp-progress-device" variants={itemReveal} aria-hidden="true">
              <div className="lp-progress-head">
                <strong>This week</strong>
                <span className="lp-progress-range"><span>‹</span>Mar 3 – Mar 9<span>›</span></span>
              </div>
              <WeekRings size={40} stroke={6} showSteps />
              <div className="lp-stat-row">
                <div className="lp-stat">
                  <span className="lp-stat-icon">{Icon.shoe}</span>
                  <span><strong>{weekTotal.toLocaleString('en-US')}</strong><small>steps this week</small></span>
                </div>
                <div className="lp-stat">
                  <span className="lp-stat-icon">{Icon.bars}</span>
                  <span><strong>{daysReached} of 7</strong><small>days reached</small></span>
                </div>
                <div className="lp-stat">
                  <span className="lp-stat-icon">{Icon.calendar}</span>
                  <span><strong>{Math.round(weekTotal / daysLogged).toLocaleString('en-US')}</strong><small>average per day</small></span>
                </div>
              </div>
            </m.div>
          </div>
        </m.section>

        <m.section id="together" className="lp-shell lp-together" {...revealProps}>
          <m.img
            variants={itemReveal}
            className="lp-together-photo"
            src={`${TOGETHER_PHOTO}?w=1100&q=70&auto=format&fit=crop`}
            srcSet={`${TOGETHER_PHOTO}?w=600&q=70&auto=format&fit=crop 600w, ${TOGETHER_PHOTO}?w=1100&q=70&auto=format&fit=crop 1100w`}
            sizes="(max-width: 900px) 100vw, 55vw"
            alt="Four friends laughing together on a walk through an autumn park"
            width="1100"
            height="619"
            loading="lazy"
            decoding="async"
          />
          <m.div variants={itemReveal}>
            <h2 className="lp-h2 lp-h2-lg">Build the habit.<br />Bring your people.</h2>
            <p className="lp-lead">Share a win, send encouragement, or join a challenge. Everyone walks toward their own goal.</p>
            <a className="lp-link" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              Explore walking together <span aria-hidden="true">→</span>
            </a>
          </m.div>
        </m.section>

        <m.section id="how-it-works" className="lp-shell" {...revealProps}>
          <div className="lp-steps">
            <m.h2 className="lp-steps-title" variants={itemReveal}>How it works</m.h2>
            {[
              { title: 'Set your goal', text: 'Choose a daily step goal that fits your day.' },
              { title: 'Let your steps sync', text: 'We connect with Apple Health to count your steps automatically.' },
              { title: 'Check in and keep going', text: 'See your progress, follow your plan, and get support from friends.' }
            ].map((step, i) => (
              <m.div className="lp-step" key={step.title} variants={itemReveal}>
                <span className="lp-step-num">{i + 1}</span>
                <span>
                  <strong>{step.title}</strong>
                  <small>{step.text}</small>
                </span>
              </m.div>
            ))}
          </div>
        </m.section>

        <m.section id="faq" className="lp-shell lp-faq" {...revealProps}>
          <m.h2 className="lp-h3" variants={itemReveal}>Frequently asked questions</m.h2>
          {FAQS.map((item) => (
            <m.details className="lp-faq-item" key={item.q} variants={itemReveal}>
              <summary>
                {item.q}
                {Icon.chevron}
              </summary>
              <p>{item.a}</p>
            </m.details>
          ))}
        </m.section>

        <m.section id="get-the-app" className="lp-shell" {...revealProps}>
          <div className="lp-cta">
            <span className="lp-cta-print is-left" aria-hidden="true">{Icon.footprints}</span>
            <span className="lp-cta-print is-right" aria-hidden="true">{Icon.footprints}</span>
            <div className="lp-cta-row">
              <m.div variants={itemReveal}>
                <h2>Your next walk starts here.</h2>
                <p>Start building a routine with Walk It Off.</p>
              </m.div>
              <m.div className="lp-cta-actions" variants={itemReveal}>
                <AppStoreBadge className="is-outlined" />
                <button
                  type="button"
                  className="lp-btn lp-btn-ghost"
                  aria-expanded={showWaitlist}
                  aria-controls="android-waitlist"
                  onClick={() => setShowWaitlist((v) => !v)}
                >
                  {Icon.android}
                  Android waitlist
                </button>
              </m.div>
            </div>

            <AnimatePresence initial={false}>
              {showWaitlist && (
                <m.div
                  id="android-waitlist"
                  className="lp-waitlist"
                  key="waitlist"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: EASE_OUT }}
                >
                  {!formSubmitted ? (
                    <form className="lp-waitlist-form" onSubmit={handleSubmit}>
                      <label htmlFor="waitlist-email" className="visually-hidden">Email address</label>
                      <input
                        id="waitlist-email"
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isSubmitting}
                      />
                      <button type="submit" className="lp-btn lp-btn-teal" disabled={isSubmitting}>
                        {isSubmitting ? 'Joining…' : 'Join the waitlist'}
                      </button>
                      <p className="lp-waitlist-note">
                        We'll email you when Walk It Off lands on Android. No spam. We never sell your information. <a href="/privacy">Privacy Policy</a>
                      </p>
                    </form>
                  ) : (
                    <p className="lp-waitlist-success" role="status">
                      You're in! We'll email you as soon as Walk It Off is available on Android.
                    </p>
                  )}
                </m.div>
              )}
            </AnimatePresence>
          </div>
        </m.section>
      </main>

      <footer className="lp-shell lp-footer">
        <a href="/" className="lp-nav-logo" aria-label="Walk It Off home">
          <img src="/walkitofflogo-dark.svg" alt="Walk It Off" width="350" height="130" loading="lazy" decoding="async" />
        </a>
        <nav className="lp-footer-links" aria-label="Footer">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/careers">Careers</a>
          <a href="/support">Support</a>
          <a href="https://www.instagram.com/walkitoff.health/" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
            </svg>
          </a>
          <a href="https://www.tiktok.com/@walkitoffhealth0" aria-label="TikTok" target="_blank" rel="noopener noreferrer">
            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
            </svg>
          </a>
        </nav>
        <div className="lp-footer-meta">
          <p>Walk It Off is a wellness app, not a medical device.</p>
          <p>© {currentYear} Walk It Off. All rights reserved.</p>
        </div>
      </footer>
    </div>
    </MotionConfig>
    </LazyMotion>
  );
}

export default App;
